import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { ReportDurationStats, ReportsPerformance, ReportsPerformanceBlock } from '../../services/api';
import { formatReportDateTime, formatReportDuration } from '../../utils/reportFormat';

interface Props {
  data: ReportsPerformance | null;
  loading: boolean;
}

const UNASSIGNED = '__unassigned__';

/**
 * Serving time / SLA view: how long replies, callbacks, visits and first
 * contact after assignment actually took — totals for the current filters,
 * then the same per agent. No targets yet: averages, medians and the slowest
 * 10% describe the team's real baseline.
 */
export const ReportsPerformancePanel: React.FC<Props> = ({ data, loading }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const units = useMemo(() => ({
    s: t('reports.duration.s'),
    m: t('reports.duration.m'),
    h: t('reports.duration.h'),
    d: t('reports.duration.d'),
  }), [t]);
  const fmt = (seconds?: number | null) => formatReportDuration(seconds, units);

  if (loading && !data) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.primary as string} />
      </View>
    );
  }
  if (!data) {
    return <Text style={styles.emptyText}>{t('reports.noData')}</Text>;
  }

  const total = data.totals;

  // One timing per block: label + average on the first line, the
  // explanation under it, then the spread on its own line — nothing has to
  // share a narrow row, so labels never get squeezed.
  const statRow = (label: string, stats: ReportDurationStats | null, hint?: string) => (
    <View style={styles.statBlock}>
      <View style={[styles.statHead, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.statLabel, { textAlign: rtl.textStart }]}>{label}</Text>
        <Text style={stats ? styles.statMain : styles.statEmpty}>{stats ? fmt(stats.avgSeconds) : '—'}</Text>
      </View>
      {!!hint && <Text style={[styles.statHint, { textAlign: rtl.textStart }]}>{hint}</Text>}
      {stats && (
        <View style={[styles.statSpread, { flexDirection: rtl.rowDirection }]}>
          <Text style={styles.statSub}>{t('reports.performance.median')} <Text style={styles.statSubValue}>{fmt(stats.medianSeconds)}</Text></Text>
          <Text style={styles.statSub}>{t('reports.performance.p90')} <Text style={styles.statSubValue}>{fmt(stats.p90Seconds)}</Text></Text>
          <Text style={styles.statSub}>{t('reports.performance.range')} <Text style={styles.statSubValue}>{fmt(stats.minSeconds)} – {fmt(stats.maxSeconds)}</Text></Text>
          <Text style={styles.statSub}>{t('reports.performance.samples', { count: stats.count })}</Text>
        </View>
      )}
    </View>
  );

  // wide: for long values like durations ("2h 19m 25s") so they're never cut
  const countPill = (label: string, value: number | string, tone: 'neutral' | 'good' | 'bad' = 'neutral', wide = false) => (
    <View style={[styles.pill, wide && styles.pillWide, tone === 'good' && styles.pillGood, tone === 'bad' && Number(value) > 0 && styles.pillBad]}>
      <Text style={[styles.pillValue, wide && styles.pillValueWide, tone === 'good' && styles.pillValueGood, tone === 'bad' && Number(value) > 0 && styles.pillValueBad]} numberOfLines={1}>{value}</Text>
      <Text style={styles.pillLabel} numberOfLines={2}>{label}</Text>
    </View>
  );

  const section = (icon: string, title: string, children: React.ReactNode) => (
    <View style={styles.card}>
      <View style={[styles.cardHeader, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name={icon as any} size={16} color={colors.primary as string} />
        <Text style={styles.cardTitle}>{title}</Text>
      </View>
      {children}
    </View>
  );

  const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

  const agentCells = (b: ReportsPerformanceBlock) => [
    String(b.activity.messagesSent),
    String(b.activity.calls),
    fmt(b.activity.callTimeTotalSeconds || 0),
    fmt(b.activity.talkTimeTotalSeconds || 0),
    String(b.activity.customersReached),
    fmt(b.activity.breakTime?.avgSeconds),
    String(b.messages.replied),
    fmt(b.messages.responseTime?.avgSeconds),
    String(b.messages.unanswered),
    `${b.calls.answered}/${b.calls.total}`,
    String(b.calls.missedInbound),
    fmt(b.calls.ringTime?.avgSeconds),
    fmt(b.calls.callbackTime?.avgSeconds),
    `${b.visits.completed}/${b.visits.total}`,
    fmt(b.visits.assignmentToStart?.avgSeconds),
    fmt(b.visits.servingTime?.avgSeconds),
    `${b.assignments.reachedInPeriod ?? 0}/${b.assignments.currentlyAssigned ?? 0}`,
    `${b.assignments.interacted}/${b.assignments.total}`,
    fmt(b.assignments.timeToFirstInteraction?.avgSeconds),
  ];
  const columns = [
    t('reports.performance.col.messagesSent'),
    t('reports.performance.col.calls'),
    t('reports.performance.col.callTimeTotal'),
    t('reports.performance.col.talkTimeTotal'),
    t('reports.performance.col.customersReached'),
    t('reports.performance.col.avgBreak'),
    t('reports.performance.col.replies'),
    t('reports.performance.col.avgResponse'),
    t('reports.performance.col.unanswered'),
    t('reports.performance.col.callsAnswered'),
    t('reports.performance.col.missed'),
    t('reports.performance.col.avgRing'),
    t('reports.performance.col.avgCallback'),
    t('reports.performance.col.visitsDone'),
    t('reports.performance.col.assignToStart'),
    t('reports.performance.col.avgServing'),
    t('reports.performance.col.assignedReached'),
    t('reports.performance.col.assignContacted'),
    t('reports.performance.col.firstInteraction'),
  ];

  return (
    <View style={styles.container}>
      <Text style={[styles.note, { textAlign: rtl.textStart }]}>
        {t('reports.performance.note')} {t('reports.performance.computedAt', { time: formatReportDateTime(data.computedAt, locale) })}
      </Text>

      <View style={[styles.cardsGrid, { flexDirection: rtl.rowDirection }]}>
        {section('pulse-outline', t('reports.performance.activity'), (
          <>
            <View style={[styles.pills, { flexDirection: rtl.rowDirection }]}>
              {countPill(t('reports.performance.messagesTotal'), total.activity.messagesTotal)}
              {countPill(t('reports.performance.messagesSent'), total.activity.messagesSent)}
              {countPill(t('reports.performance.messagesReceived'), total.activity.messagesReceived)}
              {countPill(t('reports.performance.messagesAutomated'), total.activity.messagesAutomated)}
              {countPill(t('reports.performance.callsMade'), total.activity.calls)}
              {countPill(t('reports.performance.callTimeTotal'), fmt(total.activity.callTimeTotalSeconds || 0), 'neutral', true)}
              {countPill(t('reports.performance.talkTimeTotal'), fmt(total.activity.talkTimeTotalSeconds || 0), 'neutral', true)}
              {countPill(t('reports.performance.breakTotal'), fmt(total.activity.breakTotalSeconds || 0), 'neutral', true)}
              {countPill(t('reports.performance.visitsCount'), total.activity.visits)}
              {countPill(t('reports.performance.customersReached'), total.activity.customersReached, 'good')}
            </View>
            {statRow(t('reports.performance.breakTime'), total.activity.breakTime, t('reports.performance.breakTimeHint'))}
            <Text style={[styles.statHint, { textAlign: rtl.textStart }]}>{t('reports.performance.activityHint')}</Text>
          </>
        ))}

        {section('chatbubbles-outline', t('reports.performance.messages'), (
          <>
            <View style={[styles.pills, { flexDirection: rtl.rowDirection }]}>
              {countPill(t('reports.performance.replied'), total.messages.replied, 'good')}
              {countPill(t('reports.performance.unanswered'), total.messages.unanswered, 'bad')}
            </View>
            {statRow(t('reports.performance.responseTime'), total.messages.responseTime, t('reports.performance.responseTimeHint'))}
            {statRow(t('reports.performance.firstResponseTime'), total.messages.firstResponseTime, t('reports.performance.firstResponseTimeHint'))}
          </>
        ))}

        {section('call-outline', t('reports.performance.calls'), (
          <>
            <View style={[styles.pills, { flexDirection: rtl.rowDirection }]}>
              {countPill(t('reports.performance.answered'), total.calls.answered, 'good')}
              {countPill(t('reports.performance.missedInbound'), total.calls.missedInbound, 'bad')}
              {countPill(t('reports.performance.calledBack'), total.calls.calledBack, 'good')}
              {countPill(t('reports.performance.notCalledBack'), total.calls.notCalledBack, 'bad')}
              {countPill(t('reports.performance.answerRate'), pct(total.calls.answered, total.calls.total))}
            </View>
            {statRow(t('reports.performance.callbackTime'), total.calls.callbackTime, t('reports.performance.callbackTimeHint'))}
            {statRow(t('reports.performance.ringTime'), total.calls.ringTime, t('reports.performance.ringTimeHint'))}
            {statRow(t('reports.performance.unansweredRingTime'), total.calls.unansweredRingTime, t('reports.performance.unansweredRingTimeHint'))}
            {statRow(t('reports.performance.talkTime'), total.calls.talkTime)}
          </>
        ))}

        {section('location-outline', t('reports.performance.visits'), (
          <>
            <View style={[styles.pills, { flexDirection: rtl.rowDirection }]}>
              {countPill(t('reports.performance.completed'), total.visits.completed, 'good')}
              {countPill(t('reports.performance.inProgress'), total.visits.inProgress)}
              {countPill(t('reports.performance.open'), total.visits.open)}
              {countPill(t('reports.performance.missedVisits'), total.visits.missed, 'bad')}
              {countPill(t('reports.performance.completionRate'), pct(total.visits.completed, total.visits.total))}
            </View>
            {statRow(t('reports.performance.assignmentToStart'), total.visits.assignmentToStart)}
            {statRow(t('reports.performance.startDelay'), total.visits.startDelay, t('reports.performance.startDelayHint'))}
            {statRow(t('reports.performance.servingTime'), total.visits.servingTime, t('reports.performance.servingTimeHint'))}
          </>
        ))}

        {section('person-add-outline', t('reports.performance.assignments'), (
          <>
            <View style={[styles.pills, { flexDirection: rtl.rowDirection }]}>
              {countPill(t('reports.performance.currentlyAssigned'), total.assignments.currentlyAssigned ?? 0)}
              {countPill(t('reports.performance.reachedInPeriod'), total.assignments.reachedInPeriod ?? 0, 'good')}
              {countPill(t('reports.performance.notReachedInPeriod'), total.assignments.notReachedInPeriod ?? 0, 'bad')}
            </View>
            <Text style={[styles.statHint, { textAlign: rtl.textStart }]}>{t('reports.performance.currentlyAssignedHint')}</Text>
            <Text style={[styles.groupTitle, { textAlign: rtl.textStart }]}>{t('reports.performance.newAssignments', { count: total.assignments.total })}</Text>
            <View style={[styles.pills, { flexDirection: rtl.rowDirection }]}>
              {countPill(t('reports.performance.contacted'), total.assignments.interacted, 'good')}
              {countPill(t('reports.performance.notContacted'), total.assignments.notInteracted, 'bad')}
            </View>
            {statRow(t('reports.performance.timeToFirstInteraction'), total.assignments.timeToFirstInteraction, t('reports.performance.timeToFirstInteractionHint'))}
            <Text style={[styles.statHint, { textAlign: rtl.textStart }]}>{t('reports.performance.assignmentsHint')}</Text>
          </>
        ))}
      </View>

      <Text style={[styles.tableTitle, { textAlign: rtl.textStart }]}>{t('reports.performance.byAgent')}</Text>
      {data.byAgent.length === 0 ? (
        <Text style={styles.emptyText}>{t('reports.noData')}</Text>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={styles.tableScroll}>
          <View>
            <View style={[styles.tr, styles.thRow, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.th, styles.agentCol]}>{t('reports.performance.col.agent')}</Text>
              {columns.map((c) => <Text key={c} style={[styles.th, styles.numCol]}>{c}</Text>)}
            </View>
            {data.byAgent.map((a, i) => (
              <View key={a.userId} style={[styles.tr, i % 2 === 1 && styles.trAlt, { flexDirection: rtl.rowDirection }]}>
                <View style={styles.agentCol}>
                  <Text style={[styles.td, styles.agentName]} numberOfLines={1}>
                    {a.userId === UNASSIGNED ? t('reports.performance.unassigned') : (a.agentName || a.userId)}
                  </Text>
                  {!!a.departmentName && <Text style={styles.agentDept} numberOfLines={1}>{a.departmentName}</Text>}
                </View>
                {agentCells(a).map((v, j) => <Text key={j} style={[styles.td, styles.numCol]}>{v}</Text>)}
              </View>
            ))}
          </View>
        </ScrollView>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  // width/maxWidth keep the wide agent table from stretching the whole panel
  container: { gap: spacing.md, paddingTop: spacing.sm, paddingBottom: spacing.lg, width: '100%', maxWidth: '100%', minWidth: 0 },
  tableScroll: { width: '100%', maxWidth: '100%' },
  centered: { padding: spacing.lg, alignItems: 'center' },
  emptyText: { color: colors.text.secondary, textAlign: 'center', padding: spacing.md },
  note: { color: colors.text.secondary, fontSize: typography.fontSize.xs },
  cardsGrid: { flexWrap: 'wrap', gap: spacing.md, width: '100%' },
  card: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 380,
    minWidth: 0,
    maxWidth: '100%',
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    gap: spacing.sm,
  },
  cardHeader: { alignItems: 'center', gap: spacing.xs, marginBottom: 2 },
  cardTitle: { fontWeight: '700', color: colors.text.primary, fontSize: typography.fontSize.base },
  // Even grid of count tiles: same width, wrap 2-4 per row
  pills: { flexWrap: 'wrap', gap: spacing.xs },
  pill: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 96,
    minWidth: 84,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
    gap: 2,
  },
  groupTitle: { color: colors.text.primary, fontWeight: '600', fontSize: typography.fontSize.sm, marginTop: spacing.sm },
  pillWide: { flexBasis: 150, minWidth: 140 },
  pillValueWide: { fontSize: typography.fontSize.base },
  pillGood: { backgroundColor: 'rgba(16,185,129,0.12)' },
  pillBad: { backgroundColor: 'rgba(239,68,68,0.12)' },
  pillValue: { fontWeight: '700', color: colors.text.primary, fontSize: typography.fontSize.lg },
  pillValueGood: { color: '#10B981' },
  pillValueBad: { color: '#EF4444' },
  pillLabel: { color: colors.text.secondary, fontSize: typography.fontSize.xs, textAlign: 'center' },
  inlineStat: { color: colors.text.primary, fontSize: typography.fontSize.sm },
  statBlock: {
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    gap: 4,
  },
  statHead: { justifyContent: 'space-between', alignItems: 'baseline', gap: spacing.sm },
  statLabel: { flex: 1, color: colors.text.primary, fontWeight: '600', fontSize: typography.fontSize.sm },
  statHint: { color: colors.text.secondary, fontSize: typography.fontSize.xs },
  statMain: { color: colors.primary, fontWeight: '700', fontSize: typography.fontSize.lg },
  statSpread: { flexWrap: 'wrap', columnGap: spacing.md, rowGap: 2 },
  statSub: { color: colors.text.secondary, fontSize: typography.fontSize.xs },
  statSubValue: { color: colors.text.primary, fontWeight: '600' },
  statEmpty: { color: colors.text.disabled, fontSize: typography.fontSize.lg },
  tableTitle: { fontWeight: '700', color: colors.text.primary, fontSize: typography.fontSize.base, marginTop: spacing.sm },
  tr: { alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  trAlt: { backgroundColor: colors.background.secondary },
  thRow: { borderBottomWidth: 1 },
  th: { color: colors.text.secondary, fontWeight: '700', fontSize: typography.fontSize.xs, paddingHorizontal: spacing.xs },
  td: { color: colors.text.primary, fontSize: typography.fontSize.sm, paddingHorizontal: spacing.xs },
  agentCol: { width: 170, paddingHorizontal: spacing.xs },
  agentName: { fontWeight: '600', paddingHorizontal: 0 },
  agentDept: { color: colors.text.secondary, fontSize: typography.fontSize.xs },
  numCol: { width: 104, textAlign: 'center' },
});
